import api from './api'; // Import the configured Axios instance

// TODO: Move these interfaces to a dedicated types file (e.g., src/types/tasks.ts)

export interface TaskOwner {
  id: number;
  name: string | null;
}

export type TaskType = 'TASK' | 'JOB';
export type TaskStatus = 'OPEN' | 'ASSIGNED' | 'CLOSED';
export type JobEmploymentType = 'FULL_TIME' | 'PART_TIME' | 'CONTRACT' | 'FREELANCE';

export interface Task {
  id: number;
  title: string;
  description: string;
  type: TaskType;
  status: TaskStatus;
  skills: string[];
  isRemote: boolean;
  locationName: string | null;
  latitude: number | null;
  longitude: number | null;
  ownerId: number;
  createdAt: string; // ISO Date string
  updatedAt: string; // ISO Date string
  deadline?: string | null; // ISO Date string
  estimatedHours?: number | null;
  employmentType?: JobEmploymentType | null;
  duration?: string | null;
  compensation?: string | null;
  owner?: TaskOwner; // Included from backend
}

export interface TaskCommentAuthor {
  id: number;
  name: string | null;
}

export interface TaskComment {
  id: number;
  content: string;
  taskId: number;
  authorId: number;
  createdAt: string; // ISO Date string
  author?: TaskCommentAuthor; // Included from backend
}

export interface TaskFilters {
  type?: TaskType;
  status?: TaskStatus;
  search?: string;
  ownerId?: number;
  distance?: '5' | '10' | '25' | '50' | 'remote';
  page?: number;
  per_page?: number;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  perPage: number;
  totalPages: number;
}

// --- Task API Functions ---

export const createTask = async (taskData: Omit<Task, 'id' | 'createdAt' | 'updatedAt' | 'ownerId' | 'owner'>): Promise<Task> => {
  const response = await api.post<Task>('/tasks', taskData);
  return response.data;
};

export const fetchTasks = async (filters: TaskFilters = {}): Promise<PaginatedResponse<Task>> => {
  const response = await api.get<PaginatedResponse<Task>>('/tasks', { params: filters });
  return response.data;
};

export const fetchTaskById = async (taskId: number): Promise<Task> => {
  const response = await api.get<Task>(`/tasks/${taskId}`);
  return response.data;
};

export const updateTask = async (taskId: number, updateData: Partial<Omit<Task, 'id' | 'createdAt' | 'updatedAt' | 'ownerId' | 'owner' | 'type'>>): Promise<Task> => {
  const response = await api.put<Task>(`/tasks/${taskId}`, updateData);
  return response.data;
};

export const deleteTask = async (taskId: number): Promise<void> => {
  await api.delete(`/tasks/${taskId}`);
};

// --- Task Comment API Functions ---

export const fetchTaskComments = async (taskId: number, page: number = 1, perPage: number = 20): Promise<PaginatedResponse<TaskComment>> => {
  const response = await api.get<PaginatedResponse<TaskComment>>(`/tasks/${taskId}/comments`, {
    params: { page, per_page: perPage }
  });
  return response.data;
};

export const addTaskComment = async (taskId: number, content: string): Promise<TaskComment> => {
  const response = await api.post<TaskComment>(`/tasks/${taskId}/comments`, { content });
  return response.data;
};

export const deleteTaskComment = async (commentId: number): Promise<void> => {
  await api.delete(`/tasks/comments/${commentId}`);
}; 